import {
  type ComplianceActivityKind,
  type ComplianceProjectStatus,
  type ComplianceProjectTrigger,
  type ComplianceTaskDecision,
  type ComplianceTaskStatus,
  type PolicyImpactLevel,
  type PolicyReviewStatus,
} from "./enums";
import { parseOneOf, type OneOfParseError } from "./guards";
import {
  knownIdOrEmptyFromInput,
  knownIdOrNullFromInput,
  type KnownIdOption,
} from "./idInput";
import { err, ok, type Result } from "./result";
import { cleanNullableText, cleanRequiredText, type NullableText } from "./text";
import { parseOptionalDateInput } from "./time";

export type ComplianceProjectError =
  | { readonly kind: "emptyProjectName" }
  | { readonly kind: "invalidProjectDueDate" }
  | { readonly kind: "emptyTaskTitle" }
  | { readonly kind: "invalidTaskDueDate" }
  | { readonly kind: "emptyComment" };

export const complianceProjectErrorMessage = (error: ComplianceProjectError): string => {
  switch (error.kind) {
    case "emptyProjectName":
      return "Project name is required";
    case "invalidProjectDueDate":
      return "Project due date must be a valid date";
    case "emptyTaskTitle":
      return "Task title is required";
    case "invalidTaskDueDate":
      return "Task due date must be a valid date";
    case "emptyComment":
      return "Comment is required";
  }
};

export const COMPLIANCE_PROJECT_STATUS_OPTIONS = [
  "draft",
  "active",
  "atRisk",
  "readyForApproval",
  "complete",
] as const satisfies readonly ComplianceProjectStatus[];

export const COMPLIANCE_PROJECT_TRIGGER_OPTIONS = [
  "providerAlert",
  "legislativeChange",
  "standardsChange",
  "periodicReview",
  "manual",
] as const satisfies readonly ComplianceProjectTrigger[];

export const COMPLIANCE_TASK_STATUS_ORDER = [
  "unassigned",
  "toReview",
  "inProgress",
  "readyForApproval",
  "approved",
  "noChangeRequired",
  "blocked",
] as const satisfies readonly ComplianceTaskStatus[];

export const POLICY_IMPACT_LEVEL_ORDER = [
  "high",
  "medium",
  "low",
] as const satisfies readonly PolicyImpactLevel[];

export const complianceProjectStatusDisplayName = (
  status: ComplianceProjectStatus,
): string => {
  const labels: Record<ComplianceProjectStatus, string> = {
    draft: "Draft",
    active: "Active",
    atRisk: "At risk",
    readyForApproval: "Ready for approval",
    complete: "Complete",
  };
  return labels[status];
};

export const complianceProjectTriggerDisplayName = (
  trigger: ComplianceProjectTrigger,
): string => {
  const labels: Record<ComplianceProjectTrigger, string> = {
    manual: "Manual",
    providerAlert: "Provider alert",
    legislativeChange: "Legislative change",
    standardsChange: "Standards change",
    periodicReview: "Periodic review",
  };
  return labels[trigger];
};

export const complianceTaskStatusDisplayName = (
  status: ComplianceTaskStatus,
): string => {
  const labels: Record<ComplianceTaskStatus, string> = {
    unassigned: "Unassigned",
    toReview: "To review",
    inProgress: "In progress",
    readyForApproval: "Ready for approval",
    approved: "Approved",
    noChangeRequired: "No change required",
    blocked: "Blocked",
  };
  return labels[status];
};

export const complianceTaskDecisionDisplayName = (
  decision: ComplianceTaskDecision | null,
): string => {
  if (decision === null) return "-";
  const labels: Record<ComplianceTaskDecision, string> = {
    policyUpdated: "Document updated",
    noChangeRequired: "No change required",
    deferred: "Deferred",
    blocked: "Blocked",
  };
  return labels[decision];
};

export const policyImpactLevelDisplayName = (
  level: PolicyImpactLevel,
): string => {
  const labels: Record<PolicyImpactLevel, string> = {
    high: "High",
    medium: "Medium",
    low: "Low",
  };
  return labels[level];
};

export const COMPLIANCE_PROJECT_DETAIL_TABS = [
  "overview",
  "workboard",
  "mapping",
  "report",
  "activity",
] as const;

export type ComplianceProjectDetailTab = (typeof COMPLIANCE_PROJECT_DETAIL_TABS)[number];

export type ComplianceProjectNotice = {
  readonly kind: "error";
  readonly text: string;
};

export const complianceProjectDetailTabDisplayName = (
  tab: ComplianceProjectDetailTab,
): string => {
  const labels: Record<ComplianceProjectDetailTab, string> = {
    overview: "Overview",
    workboard: "Workboard",
    mapping: "Document mapping",
    report: "Report",
    activity: "Activity",
  };
  return labels[tab];
};

export const complianceProjectErrorNotice = (text: string): ComplianceProjectNotice => ({
  kind: "error",
  text,
});

export type ProjectCreateSubmissionState = {
  readonly saving: boolean;
  readonly notice: ComplianceProjectNotice | null;
};

export const emptyProjectCreateSubmissionState = (): ProjectCreateSubmissionState => ({
  saving: false,
  notice: null,
});

export type ProjectCreateSubmissionAction =
  | { readonly type: "started" }
  | { readonly type: "succeeded" }
  | { readonly type: "failed"; readonly error: string };

export const nextProjectCreateSubmissionState = (
  _current: ProjectCreateSubmissionState,
  action: ProjectCreateSubmissionAction,
): ProjectCreateSubmissionState => {
  switch (action.type) {
    case "started":
      return { saving: true, notice: null };
    case "succeeded":
      return { saving: false, notice: null };
    case "failed":
      return { saving: false, notice: complianceProjectErrorNotice(action.error) };
  }
};

export type ComplianceProjectDetailSubmission = "manualTask" | "comment";

export type ComplianceProjectDetailActionState =
  | { readonly kind: "idle"; readonly notice: null; readonly submitting: null }
  | { readonly kind: "submitting"; readonly notice: null; readonly submitting: ComplianceProjectDetailSubmission }
  | { readonly kind: "failed"; readonly notice: ComplianceProjectNotice; readonly submitting: null };

export const emptyComplianceProjectDetailActionState = (): ComplianceProjectDetailActionState => ({
  kind: "idle",
  notice: null,
  submitting: null,
});

export type ComplianceProjectDetailAction =
  | { readonly type: "cleared" }
  | { readonly type: "started"; readonly submission: ComplianceProjectDetailSubmission }
  | { readonly type: "succeeded" }
  | { readonly type: "failed"; readonly error: string };

export const nextComplianceProjectDetailActionState = (
  _current: ComplianceProjectDetailActionState,
  action: ComplianceProjectDetailAction,
): ComplianceProjectDetailActionState => {
  switch (action.type) {
    case "cleared":
    case "succeeded":
      return emptyComplianceProjectDetailActionState();
    case "started":
      return { kind: "submitting", notice: null, submitting: action.submission };
    case "failed":
      return { kind: "failed", notice: complianceProjectErrorNotice(action.error), submitting: null };
  }
};

export const complianceProjectDetailActionNotice = (
  state: ComplianceProjectDetailActionState,
): ComplianceProjectNotice | null => state.notice;

export const complianceProjectDetailActionSubmitting = (
  state: ComplianceProjectDetailActionState,
  submission: ComplianceProjectDetailSubmission,
): boolean => state.submitting === submission;

export const complianceProjectDetailActionButtonLabel = (
  state: ComplianceProjectDetailActionState,
  submission: ComplianceProjectDetailSubmission,
  labels: { readonly idle: string; readonly submitting: string },
): string => complianceProjectDetailActionSubmitting(state, submission) ? labels.submitting : labels.idle;

export const complianceProjectNoticeToneClass = (notice: ComplianceProjectNotice): string => {
  switch (notice.kind) {
    case "error":
      return "bg-red-50 text-red-700 ring-red-100";
  }
};

export type ComplianceProjectMetricTone = "slate" | "blue" | "amber" | "green";

const COMPLIANCE_PROJECT_METRIC_TONE_CLASSES = {
  slate: "text-slate-900",
  blue: "text-sky-700",
  amber: "text-amber-700",
  green: "text-emerald-700",
} satisfies Record<ComplianceProjectMetricTone, string>;

export const complianceProjectMetricToneClass = (tone: ComplianceProjectMetricTone): string =>
  COMPLIANCE_PROJECT_METRIC_TONE_CLASSES[tone];

export const complianceProjectStatusToneClass = (
  status: ComplianceProjectStatus,
): string => {
  const tones: Record<ComplianceProjectStatus, string> = {
    draft: "bg-slate-50 text-slate-600 ring-slate-200",
    active: "bg-sky-50 text-sky-700 ring-sky-200",
    atRisk: "bg-amber-50 text-amber-700 ring-amber-200",
    readyForApproval: "bg-violet-50 text-violet-700 ring-violet-200",
    complete: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  };
  return tones[status];
};

export const complianceTaskStatusToneClass = (
  status: ComplianceTaskStatus,
): string => {
  const tones: Record<ComplianceTaskStatus, string> = {
    unassigned: "bg-slate-50 text-slate-600 ring-slate-200",
    toReview: "bg-amber-50 text-amber-700 ring-amber-200",
    inProgress: "bg-sky-50 text-sky-700 ring-sky-200",
    readyForApproval: "bg-violet-50 text-violet-700 ring-violet-200",
    approved: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    noChangeRequired: "bg-slate-100 text-slate-600 ring-slate-200",
    blocked: "bg-red-50 text-red-700 ring-red-200",
  };
  return tones[status];
};

export const policyImpactLevelToneClass = (
  level: PolicyImpactLevel,
): string => {
  const tones: Record<PolicyImpactLevel, string> = {
    high: "bg-red-50 text-red-700 ring-red-200",
    medium: "bg-amber-50 text-amber-700 ring-amber-200",
    low: "bg-slate-50 text-slate-600 ring-slate-200",
  };
  return tones[level];
};


export const nullableAssigneeIdFromDraft = <UserId extends string>(assigneeUserId: UserId | ""): UserId | null =>
  assigneeUserId === "" ? null : assigneeUserId;

export const complianceProjectStatusInputResult = (
  value: string,
): Result<ComplianceProjectStatus, OneOfParseError> =>
  parseOneOf(COMPLIANCE_PROJECT_STATUS_OPTIONS, value);

export const complianceProjectStatusFromInput = (
  value: string,
  fallback: ComplianceProjectStatus,
): ComplianceProjectStatus => {
  const parsed = complianceProjectStatusInputResult(value);
  return parsed.ok ? parsed.value : fallback;
};

export type ComplianceProjectStatusPatch = {
  readonly status: ComplianceProjectStatus;
};

export type ComplianceProjectScopeOption<ScopeId extends string = string> = KnownIdOption<ScopeId>;

export type ComplianceProjectLeadOption<UserId extends string = string> = KnownIdOption<UserId>;

export type ComplianceProjectScopePatch<ScopeId extends string = string> = {
  readonly businessScopeId: ScopeId | null;
};

export type ComplianceProjectLeadPatch<UserId extends string = string> = {
  readonly projectLeadUserId: UserId | null;
};

export const complianceProjectStatusPatchFromInput = ({
  value,
  fallback,
}: {
  readonly value: string;
  readonly fallback: ComplianceProjectStatus;
}): ComplianceProjectStatusPatch => ({
  status: complianceProjectStatusFromInput(value, fallback),
});

export const complianceProjectScopePatchFromInput = <ScopeId extends string>({
  scopes,
  value,
  fallback,
}: {
  readonly scopes: readonly ComplianceProjectScopeOption<ScopeId>[];
  readonly value: string;
  readonly fallback: ScopeId | null;
}): ComplianceProjectScopePatch<ScopeId> => ({
  businessScopeId: knownIdOrNullFromInput(scopes, value, fallback),
});

export const complianceProjectCreateScopeIdFromInput = <ScopeId extends string>({
  scopes,
  value,
  fallback,
}: {
  readonly scopes: readonly ComplianceProjectScopeOption<ScopeId>[];
  readonly value: string;
  readonly fallback: ScopeId | "";
}): ScopeId | "" => knownIdOrEmptyFromInput(scopes, value, fallback);

export const complianceProjectLeadPatchFromInput = <UserId extends string>({
  team,
  value,
  fallback,
}: {
  readonly team: readonly ComplianceProjectLeadOption<UserId>[];
  readonly value: string;
  readonly fallback: UserId | null;
}): ComplianceProjectLeadPatch<UserId> => ({
  projectLeadUserId: knownIdOrNullFromInput(team, value, fallback),
});

export const complianceTaskStatusInputResult = (
  value: string,
): Result<ComplianceTaskStatus, OneOfParseError> =>
  parseOneOf(COMPLIANCE_TASK_STATUS_ORDER, value);

export const complianceTaskStatusFromInput = (
  value: string,
  fallback: ComplianceTaskStatus,
): ComplianceTaskStatus => {
  const parsed = complianceTaskStatusInputResult(value);
  return parsed.ok ? parsed.value : fallback;
};

export const policyImpactLevelInputResult = (
  value: string,
): Result<PolicyImpactLevel, OneOfParseError> =>
  parseOneOf(POLICY_IMPACT_LEVEL_ORDER, value);

export const policyImpactLevelFromInput = (
  value: string,
  fallback: PolicyImpactLevel,
): PolicyImpactLevel => {
  const parsed = policyImpactLevelInputResult(value);
  return parsed.ok ? parsed.value : fallback;
};

export type ComplianceTaskStatusPatch = {
  readonly status: ComplianceTaskStatus;
};

export type ComplianceTaskUserOption<UserId extends string = string> = KnownIdOption<UserId>;

export type ComplianceTaskUserPatch<UserId extends string = string> =
  | { readonly assigneeUserId: UserId | null }
  | { readonly reviewerUserId: UserId | null };

export const complianceTaskStatusPatchFromInput = ({
  value,
  fallback,
}: {
  readonly value: string;
  readonly fallback: ComplianceTaskStatus;
}): ComplianceTaskStatusPatch => ({
  status: complianceTaskStatusFromInput(value, fallback),
});

export const complianceTaskAssigneePatchFromInput = <UserId extends string>({
  team,
  value,
  fallback,
}: {
  readonly team: readonly ComplianceTaskUserOption<UserId>[];
  readonly value: string;
  readonly fallback: UserId | null;
}): ComplianceTaskUserPatch<UserId> => ({
  assigneeUserId: knownIdOrNullFromInput(team, value, fallback),
});

export const complianceTaskReviewerPatchFromInput = <UserId extends string>({
  team,
  value,
  fallback,
}: {
  readonly team: readonly ComplianceTaskUserOption<UserId>[];
  readonly value: string;
  readonly fallback: UserId | null;
}): ComplianceTaskUserPatch<UserId> => ({
  reviewerUserId: knownIdOrNullFromInput(team, value, fallback),
});

export const complianceTaskUserIdOrEmptyFromInput = <UserId extends string>({
  team,
  value,
  fallback,
}: {
  readonly team: readonly ComplianceTaskUserOption<UserId>[];
  readonly value: string;
  readonly fallback: UserId | "";
}): UserId | "" => knownIdOrEmptyFromInput(team, value, fallback);

const cleanRequired = (
  value: string,
  error: ComplianceProjectError,
): Result<string, ComplianceProjectError> => cleanRequiredText(value, error);

export type ProjectCreateDraft<ScopeId extends string = string> = {
  readonly name: string;
  readonly businessScopeId?: ScopeId | "";
  readonly trigger: ComplianceProjectTrigger;
  readonly sourceSummary: string;
  readonly dueDate: string;
};

export const emptyProjectCreateFormState = <ScopeId extends string = string>(): ProjectCreateDraft<ScopeId> => ({
  name: "",
  businessScopeId: "",
  trigger: "providerAlert",
  sourceSummary: "",
  dueDate: "",
});

export type ProjectCreateFormAction<ScopeId extends string = string> =
  | { readonly type: "nameInput"; readonly value: string }
  | { readonly type: "businessScopeInput"; readonly scopes: readonly ComplianceProjectScopeOption<ScopeId>[]; readonly value: string }
  | { readonly type: "selectBusinessScope"; readonly scopeId: ScopeId | "" }
  | { readonly type: "triggerInput"; readonly value: string }
  | { readonly type: "sourceSummaryInput"; readonly value: string }
  | { readonly type: "dueDateInput"; readonly value: string };

export const nextProjectCreateFormState = <ScopeId extends string>(
  current: ProjectCreateDraft<ScopeId>,
  action: ProjectCreateFormAction<ScopeId>,
): ProjectCreateDraft<ScopeId> => {
  switch (action.type) {
    case "nameInput":
      return { ...current, name: action.value };
    case "businessScopeInput":
      return {
        ...current,
        businessScopeId: complianceProjectCreateScopeIdFromInput({
          scopes: action.scopes,
          value: action.value,
          fallback: current.businessScopeId ?? "",
        }),
      };
    case "selectBusinessScope":
      return { ...current, businessScopeId: action.scopeId };
    case "triggerInput":
      return { ...current, trigger: complianceProjectTriggerFromInput(action.value, current.trigger) };
    case "sourceSummaryInput":
      return { ...current, sourceSummary: action.value };
    case "dueDateInput":
      return { ...current, dueDate: action.value };
  }
};

export const projectCreateFormSubmittedState = <ScopeId extends string>(
  current: ProjectCreateDraft<ScopeId>,
): ProjectCreateDraft<ScopeId> => ({
  ...current,
  name: "",
  sourceSummary: "",
  dueDate: "",
});

export type PreparedProjectCreateDraft<ScopeId extends string = string> = {
  readonly name: string;
  readonly businessScopeId: ScopeId | null;
  readonly trigger: ComplianceProjectTrigger;
  readonly sourceSummary: string | null;
  readonly dueAt: number | null;
};

export const complianceProjectTriggerInputResult = (
  value: string,
): Result<ComplianceProjectTrigger, OneOfParseError> =>
  parseOneOf(COMPLIANCE_PROJECT_TRIGGER_OPTIONS, value);

export const complianceProjectTriggerFromInput = (
  value: string,
  fallback: ComplianceProjectTrigger,
): ComplianceProjectTrigger => {
  const parsed = complianceProjectTriggerInputResult(value);
  return parsed.ok ? parsed.value : fallback;
};

export const isProjectCreateDraftTouched = <ScopeId extends string>(draft: ProjectCreateDraft<ScopeId>): boolean =>
  draft.name.trim() !== "" ||
  draft.sourceSummary.trim() !== "" ||
  draft.dueDate.trim() !== "" ||
  (draft.businessScopeId ?? "") !== "";

export const prepareProjectCreateDraft = <ScopeId extends string>(
  draft: ProjectCreateDraft<ScopeId>,
): Result<PreparedProjectCreateDraft<ScopeId>, ComplianceProjectError> => {
  const name = cleanRequired(draft.name, { kind: "emptyProjectName" });
  if (!name.ok) return name;

  const dueAt = parseOptionalInputDate(draft.dueDate, { kind: "invalidProjectDueDate" });
  if (!dueAt.ok) return dueAt;

  return ok({
    name: name.value,
    businessScopeId: draft.businessScopeId === undefined || draft.businessScopeId === "" ? null : draft.businessScopeId,
    trigger: draft.trigger,
    sourceSummary: cleanNullableText(draft.sourceSummary),
    dueAt: dueAt.value,
  });
};

export type ComplianceProjectCreateFormInput<ScopeId extends string = string> = {
  readonly draft: ProjectCreateDraft<ScopeId>;
  readonly hasCompany: boolean;
  readonly saving: boolean;
};

export type ComplianceProjectCreateFormView<ScopeId extends string = string> = {
  readonly prepared: Result<PreparedProjectCreateDraft<ScopeId>, ComplianceProjectError>;
  readonly touched: boolean;
  readonly problem: string | null;
  readonly canSubmit: boolean;
  readonly buttonLabel: string;
};

export const complianceProjectCreateFormView = <ScopeId extends string>(
  input: ComplianceProjectCreateFormInput<ScopeId>,
): ComplianceProjectCreateFormView<ScopeId> => {
  const prepared = prepareProjectCreateDraft(input.draft);
  const touched = isProjectCreateDraftTouched(input.draft);
  return {
    prepared,
    touched,
    problem: !prepared.ok && touched ? complianceProjectErrorMessage(prepared.error) : null,
    canSubmit: input.hasCompany && prepared.ok && !input.saving,
    buttonLabel: input.saving ? "Creating..." : "Create project",
  };
};

export type NewComplianceProject<CompanyId extends string, UserId extends string, ScopeId extends string = string> = {
  readonly companyId: CompanyId;
  readonly businessScopeId: ScopeId | null;
  readonly name: string;
  readonly description: string | null;
  readonly trigger: ComplianceProjectTrigger;
  readonly sourceSummary: string | null;
  readonly status: ComplianceProjectStatus;
  readonly projectLeadUserId: UserId | null;
  readonly dueAt: number | null;
  readonly createdByUserId: UserId | null;
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly completedAt: number | null;
};

export type CreateProjectInput<CompanyId extends string, UserId extends string, ScopeId extends string = string> = {
  readonly companyId: CompanyId;
  readonly businessScopeId?: ScopeId | null;
  readonly name: string;
  readonly description?: NullableText;
  readonly trigger?: ComplianceProjectTrigger;
  readonly sourceSummary?: NullableText;
  readonly status?: ComplianceProjectStatus;
  readonly projectLeadUserId?: UserId | null;
  readonly dueAt?: number | null;
  readonly createdByUserId: UserId | null;
  readonly now: number;
};

export const prepareProjectCreate = <CompanyId extends string, UserId extends string, ScopeId extends string = string>(
  input: CreateProjectInput<CompanyId, UserId, ScopeId>,
): Result<NewComplianceProject<CompanyId, UserId, ScopeId>, ComplianceProjectError> => {
  const name = cleanRequired(input.name, { kind: "emptyProjectName" });
  if (!name.ok) return name;

  return ok({
    companyId: input.companyId,
    businessScopeId: input.businessScopeId ?? null,
    name: name.value,
    description: cleanNullableText(input.description),
    trigger: input.trigger ?? "manual",
    sourceSummary: cleanNullableText(input.sourceSummary),
    status: input.status ?? "draft",
    projectLeadUserId: input.projectLeadUserId ?? null,
    dueAt: input.dueAt ?? null,
    createdByUserId: input.createdByUserId,
    createdAt: input.now,
    updatedAt: input.now,
    completedAt: null,
  });
};

export type ComplianceProjectPatch<UserId extends string, ScopeId extends string = string> = {
  updatedAt: number;
  businessScopeId?: ScopeId | null;
  name?: string;
  description?: string | null;
  trigger?: ComplianceProjectTrigger;
  sourceSummary?: string | null;
  status?: ComplianceProjectStatus;
  projectLeadUserId?: UserId | null;
  dueAt?: number | null;
  completedAt?: number | null;
};

export type ComplianceProjectDueDatePatch = {
  readonly dueAt: number | null;
};

export const prepareProjectDueDatePatch = (
  value: string,
): Result<ComplianceProjectDueDatePatch, ComplianceProjectError> => {
  const dueAt = parseOptionalInputDate(value, { kind: "invalidProjectDueDate" });
  return dueAt.ok ? ok({ dueAt: dueAt.value }) : dueAt;
};

export type UpdateProjectInput<UserId extends string, ScopeId extends string = string> = {
  readonly businessScopeId?: ScopeId | null;
  readonly name?: string;
  readonly description?: NullableText;
  readonly trigger?: ComplianceProjectTrigger;
  readonly sourceSummary?: NullableText;
  readonly status?: ComplianceProjectStatus;
  readonly projectLeadUserId?: UserId | null;
  readonly dueAt?: number | null;
  readonly now: number;
};

export const prepareProjectUpdate = <UserId extends string, ScopeId extends string = string>(
  input: UpdateProjectInput<UserId, ScopeId>,
): Result<ComplianceProjectPatch<UserId, ScopeId>, ComplianceProjectError> => {
  const patch: ComplianceProjectPatch<UserId, ScopeId> = { updatedAt: input.now };

  if (input.businessScopeId !== undefined) patch.businessScopeId = input.businessScopeId;
  if (input.name !== undefined) {
    const name = cleanRequired(input.name, { kind: "emptyProjectName" });
    if (!name.ok) return name;
    patch.name = name.value;
  }
  if (input.description !== undefined) patch.description = cleanNullableText(input.description);
  if (input.trigger !== undefined) patch.trigger = input.trigger;
  if (input.sourceSummary !== undefined) patch.sourceSummary = cleanNullableText(input.sourceSummary);
  if (input.status !== undefined) {
    patch.status = input.status;
    patch.completedAt = input.status === "complete" ? input.now : null;
  }
  if (input.projectLeadUserId !== undefined) patch.projectLeadUserId = input.projectLeadUserId;
  if (input.dueAt !== undefined) patch.dueAt = input.dueAt;

  return ok(patch);
};

export type ManualTaskDraft<UserId extends string> = {
  readonly title: string;
  readonly description: string;
  readonly assigneeUserId: UserId | "";
  readonly dueDate: string;
  readonly impactLevel: PolicyImpactLevel;
};

export const emptyManualTaskFormState = <UserId extends string = string>(): ManualTaskDraft<UserId> => ({
  title: "",
  description: "",
  assigneeUserId: "",
  dueDate: "",
  impactLevel: "medium",
});

export type ManualTaskFormAction<UserId extends string = string> =
  | { readonly type: "titleInput"; readonly value: string }
  | { readonly type: "descriptionInput"; readonly value: string }
  | { readonly type: "assigneeInput"; readonly team: readonly ComplianceTaskUserOption<UserId>[]; readonly value: string }
  | { readonly type: "dueDateInput"; readonly value: string }
  | { readonly type: "impactLevelInput"; readonly value: string };

export const nextManualTaskFormState = <UserId extends string>(
  current: ManualTaskDraft<UserId>,
  action: ManualTaskFormAction<UserId>,
): ManualTaskDraft<UserId> => {
  switch (action.type) {
    case "titleInput":
      return { ...current, title: action.value };
    case "descriptionInput":
      return { ...current, description: action.value };
    case "assigneeInput":
      return {
        ...current,
        assigneeUserId: complianceTaskUserIdOrEmptyFromInput({
          team: action.team,
          value: action.value,
          fallback: current.assigneeUserId,
        }),
      };
    case "dueDateInput":
      return { ...current, dueDate: action.value };
    case "impactLevelInput":
      return { ...current, impactLevel: policyImpactLevelFromInput(action.value, current.impactLevel) };
  }
};

export type PreparedManualTaskDraft<UserId extends string> = {
  readonly title: string;
  readonly description: string | null;
  readonly assigneeUserId: UserId | null;
  readonly dueAt: number | null;
  readonly impactLevel: PolicyImpactLevel;
};

export const isManualTaskDraftTouched = <UserId extends string>(
  draft: ManualTaskDraft<UserId>,
): boolean =>
  draft.title.trim() !== "" ||
  draft.description.trim() !== "" ||
  draft.assigneeUserId !== "" ||
  draft.dueDate.trim() !== "" ||
  draft.impactLevel !== "medium";

export const prepareManualTaskDraft = <UserId extends string>(
  draft: ManualTaskDraft<UserId>,
): Result<PreparedManualTaskDraft<UserId>, ComplianceProjectError> => {
  const title = cleanRequired(draft.title, { kind: "emptyTaskTitle" });
  if (!title.ok) return title;

  const dueAt = parseOptionalInputDate(draft.dueDate, { kind: "invalidTaskDueDate" });
  if (!dueAt.ok) return dueAt;

  return ok({
    title: title.value,
    description: cleanNullableText(draft.description),
    assigneeUserId: draft.assigneeUserId === "" ? null : draft.assigneeUserId,
    dueAt: dueAt.value,
    impactLevel: draft.impactLevel,
  });
};

export type ManualTaskFormInput<UserId extends string> = {
  readonly draft: ManualTaskDraft<UserId>;
};

export type ManualTaskFormView<UserId extends string> = {
  readonly prepared: Result<PreparedManualTaskDraft<UserId>, ComplianceProjectError>;
  readonly touched: boolean;
  readonly problem: string | null;
  readonly canSubmit: boolean;
  readonly buttonLabel: "Add task";
};

export const manualTaskFormView = <UserId extends string>(
  input: ManualTaskFormInput<UserId>,
): ManualTaskFormView<UserId> => {
  const prepared = prepareManualTaskDraft(input.draft);
  const touched = isManualTaskDraftTouched(input.draft);
  return {
    prepared,
    touched,
    problem: !prepared.ok && touched ? complianceProjectErrorMessage(prepared.error) : null,
    canSubmit: prepared.ok,
    buttonLabel: "Add task",
  };
};

export type NewComplianceTask<
  CompanyId extends string,
  ProjectId extends string,
  ScopeId extends string,
  MatchId extends string,
  PolicyId extends string,
  SectionId extends string,
  UpdateLogId extends string,
  LegislationId extends string,
  UserId extends string,
> = {
  readonly companyId: CompanyId;
  readonly businessScopeId: ScopeId | null;
  readonly projectId: ProjectId;
  readonly policyImpactMatchId: MatchId | null;
  readonly policyId: PolicyId | null;
  readonly policySectionId: SectionId | null;
  readonly updateLogId: UpdateLogId | null;
  readonly legislationId: LegislationId | null;
  readonly title: string;
  readonly description: string | null;
  readonly status: ComplianceTaskStatus;
  readonly impactLevel: PolicyImpactLevel;
  readonly assigneeUserId: UserId | null;
  readonly reviewerUserId: UserId | null;
  readonly dueAt: number | null;
  readonly decision: ComplianceTaskDecision | null;
  readonly decisionNote: string | null;
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly completedAt: number | null;
};

export type CreateManualTaskInput<
  CompanyId extends string,
  ProjectId extends string,
  ScopeId extends string,
  PolicyId extends string,
  SectionId extends string,
  UpdateLogId extends string,
  LegislationId extends string,
  UserId extends string,
> = {
  readonly companyId: CompanyId;
  readonly projectId: ProjectId;
  readonly businessScopeId?: ScopeId | null;
  readonly projectDueAt: number | null;
  readonly title: string;
  readonly description?: NullableText;
  readonly impactLevel?: PolicyImpactLevel;
  readonly policyId?: PolicyId | null;
  readonly policySectionId?: SectionId | null;
  readonly updateLogId?: UpdateLogId | null;
  readonly legislationId?: LegislationId | null;
  readonly assigneeUserId?: UserId | null;
  readonly reviewerUserId?: UserId | null;
  readonly dueAt?: number | null;
  readonly now: number;
};

export const initialTaskStatus = (
  assigneeUserId: string | null | undefined,
): ComplianceTaskStatus => (assigneeUserId == null ? "unassigned" : "toReview");

export const prepareManualTaskCreate = <
  CompanyId extends string,
  ProjectId extends string,
  ScopeId extends string,
  PolicyId extends string,
  SectionId extends string,
  UpdateLogId extends string,
  LegislationId extends string,
  UserId extends string,
>(
  input: CreateManualTaskInput<
    CompanyId,
    ProjectId,
    ScopeId,
    PolicyId,
    SectionId,
    UpdateLogId,
    LegislationId,
    UserId
  >,
): Result<
  NewComplianceTask<
    CompanyId,
    ProjectId,
    ScopeId,
    never,
    PolicyId,
    SectionId,
    UpdateLogId,
    LegislationId,
    UserId
  >,
  ComplianceProjectError
> => {
  const title = cleanRequired(input.title, { kind: "emptyTaskTitle" });
  if (!title.ok) return title;

  return ok({
    companyId: input.companyId,
    businessScopeId: input.businessScopeId ?? null,
    projectId: input.projectId,
    policyImpactMatchId: null,
    policyId: input.policyId ?? null,
    policySectionId: input.policySectionId ?? null,
    updateLogId: input.updateLogId ?? null,
    legislationId: input.legislationId ?? null,
    title: title.value,
    description: cleanNullableText(input.description),
    status: initialTaskStatus(input.assigneeUserId),
    impactLevel: input.impactLevel ?? "medium",
    assigneeUserId: input.assigneeUserId ?? null,
    reviewerUserId: input.reviewerUserId ?? null,
    dueAt: input.dueAt ?? input.projectDueAt,
    decision: null,
    decisionNote: null,
    createdAt: input.now,
    updatedAt: input.now,
    completedAt: null,
  });
};

export type CreateImpactTaskInput<
  CompanyId extends string,
  ProjectId extends string,
  ScopeId extends string,
  MatchId extends string,
  PolicyId extends string,
  SectionId extends string,
  UpdateLogId extends string,
  LegislationId extends string,
  UserId extends string,
> = {
  readonly companyId: CompanyId;
  readonly projectId: ProjectId;
  readonly businessScopeId?: ScopeId | null;
  readonly policyImpactMatchId: MatchId;
  readonly policyId: PolicyId;
  readonly policySectionId: SectionId | null;
  readonly updateLogId: UpdateLogId;
  readonly legislationId: LegislationId;
  readonly policyTitle: string | null;
  readonly policySectionHeading: string | null;
  readonly updateSummary: string | null;
  readonly matchRationale: string;
  readonly legislationTitle: string | null;
  readonly impactLevel: PolicyImpactLevel;
  readonly matchDueAt: number | null;
  readonly projectDueAt: number | null;
  readonly assigneeUserId?: UserId | null;
  readonly reviewerUserId?: UserId | null;
  readonly dueAt?: number | null;
  readonly now: number;
};

const joinPresent = (parts: readonly (string | null)[], separator: string): string =>
  parts.filter((part): part is string => part !== null && part !== "").join(separator);

export const impactTaskTitle = (
  policyTitle: string | null,
  policySectionHeading: string | null,
): string => joinPresent([policyTitle ?? "Document review", policySectionHeading], " - ");

export const impactTaskDescription = (
  updateSummary: string | null,
  matchRationale: string,
  legislationTitle: string | null,
): string | null =>
  cleanNullableText(
    joinPresent(
      [
        updateSummary ?? matchRationale,
        legislationTitle === null ? null : `Source: ${legislationTitle}`,
      ],
      "\n",
    ),
  );

export const prepareImpactTaskCreate = <
  CompanyId extends string,
  ProjectId extends string,
  ScopeId extends string,
  MatchId extends string,
  PolicyId extends string,
  SectionId extends string,
  UpdateLogId extends string,
  LegislationId extends string,
  UserId extends string,
>(
  input: CreateImpactTaskInput<
    CompanyId,
    ProjectId,
    ScopeId,
    MatchId,
    PolicyId,
    SectionId,
    UpdateLogId,
    LegislationId,
    UserId
  >,
): NewComplianceTask<
  CompanyId,
  ProjectId,
  ScopeId,
  MatchId,
  PolicyId,
  SectionId,
  UpdateLogId,
  LegislationId,
  UserId
> => ({
  companyId: input.companyId,
  businessScopeId: input.businessScopeId ?? null,
  projectId: input.projectId,
  policyImpactMatchId: input.policyImpactMatchId,
  policyId: input.policyId,
  policySectionId: input.policySectionId,
  updateLogId: input.updateLogId,
  legislationId: input.legislationId,
  title: impactTaskTitle(input.policyTitle, input.policySectionHeading),
  description: impactTaskDescription(
    input.updateSummary,
    input.matchRationale,
    input.legislationTitle,
  ),
  status: initialTaskStatus(input.assigneeUserId),
  impactLevel: input.impactLevel,
  assigneeUserId: input.assigneeUserId ?? null,
  reviewerUserId: input.reviewerUserId ?? null,
  dueAt: input.dueAt ?? input.matchDueAt ?? input.projectDueAt,
  decision: null,
  decisionNote: null,
  createdAt: input.now,
  updatedAt: input.now,
  completedAt: null,
});

export type ComplianceTaskPatch<UserId extends string> = {
  updatedAt: number;
  title?: string;
  description?: string | null;
  status?: ComplianceTaskStatus;
  impactLevel?: PolicyImpactLevel;
  assigneeUserId?: UserId | null;
  reviewerUserId?: UserId | null;
  dueAt?: number | null;
  decision?: ComplianceTaskDecision | null;
  decisionNote?: string | null;
  completedAt?: number | null;
};

export type UpdateTaskInput<UserId extends string> = {
  readonly currentDecision: ComplianceTaskDecision | null;
  readonly title?: string;
  readonly description?: NullableText;
  readonly status?: ComplianceTaskStatus;
  readonly impactLevel?: PolicyImpactLevel;
  readonly assigneeUserId?: UserId | null;
  readonly reviewerUserId?: UserId | null;
  readonly dueAt?: number | null;
  readonly decision?: ComplianceTaskDecision | null;
  readonly decisionNote?: NullableText;
  readonly now: number;
};

export const prepareTaskUpdate = <UserId extends string>(
  input: UpdateTaskInput<UserId>,
): Result<ComplianceTaskPatch<UserId>, ComplianceProjectError> => {
  const patch: ComplianceTaskPatch<UserId> = { updatedAt: input.now };

  if (input.title !== undefined) {
    const title = cleanRequired(input.title, { kind: "emptyTaskTitle" });
    if (!title.ok) return title;
    patch.title = title.value;
  }
  if (input.description !== undefined) patch.description = cleanNullableText(input.description);
  if (input.status !== undefined) {
    patch.status = input.status;
    patch.completedAt = taskStatusDone(input.status) ? input.now : null;
    if (input.decision === undefined) {
      patch.decision = decisionForStatus(input.status, input.currentDecision);
    }
  }
  if (input.impactLevel !== undefined) patch.impactLevel = input.impactLevel;
  if (input.assigneeUserId !== undefined) patch.assigneeUserId = input.assigneeUserId;
  if (input.reviewerUserId !== undefined) patch.reviewerUserId = input.reviewerUserId;
  if (input.dueAt !== undefined) patch.dueAt = input.dueAt;
  if (input.decision !== undefined) patch.decision = input.decision;
  if (input.decisionNote !== undefined) patch.decisionNote = cleanNullableText(input.decisionNote);

  return ok(patch);
};

export const prepareCommentBody = (
  body: string,
): Result<string, ComplianceProjectError> => cleanRequired(body, { kind: "emptyComment" });

export type ProjectCommentFormInput = {
  readonly body: string;
};

export type ProjectCommentFormView = {
  readonly prepared: Result<string, ComplianceProjectError>;
  readonly touched: boolean;
  readonly problem: string | null;
  readonly canSubmit: boolean;
  readonly buttonLabel: "Add comment";
};

export const projectCommentFormView = ({
  body,
}: ProjectCommentFormInput): ProjectCommentFormView => {
  const prepared = prepareCommentBody(body);
  const touched = body.trim() !== "";
  return {
    prepared,
    touched,
    problem: !prepared.ok && touched ? complianceProjectErrorMessage(prepared.error) : null,
    canSubmit: prepared.ok,
    buttonLabel: "Add comment",
  };
};

export type ComplianceTaskReviewTarget<MatchId extends string = string> =
  | { readonly kind: "manual" }
  | { readonly kind: "policyReview"; readonly matchId: MatchId };

export const complianceTaskReviewTarget = <MatchId extends string>(task: {
  readonly policyImpactMatchId: MatchId | null;
}): ComplianceTaskReviewTarget<MatchId> =>
  task.policyImpactMatchId === null
    ? { kind: "manual" }
    : { kind: "policyReview", matchId: task.policyImpactMatchId };

export type ProjectTimestampPatch = {
  readonly updatedAt: number;
};

export const projectTimestampPatch = (now: number): ProjectTimestampPatch => ({
  updatedAt: now,
});

export type ProjectActivityInsertInput<
  CompanyId extends string,
  ProjectId extends string,
  TaskId extends string,
  UserId extends string,
  ScopeId extends string = string,
> = {
  readonly companyId: CompanyId;
  readonly projectId: ProjectId;
  readonly projectBusinessScopeId?: ScopeId | null;
  readonly taskId: TaskId | null;
  readonly actorUserId: UserId | null;
  readonly kind: ComplianceActivityKind;
  readonly body: string;
  readonly now: number;
};

export type ProjectActivityInsert<
  CompanyId extends string,
  ProjectId extends string,
  TaskId extends string,
  UserId extends string,
  ScopeId extends string = string,
> = {
  readonly companyId: CompanyId;
  readonly businessScopeId: ScopeId | null;
  readonly projectId: ProjectId;
  readonly taskId: TaskId | null;
  readonly actorUserId: UserId | null;
  readonly kind: ComplianceActivityKind;
  readonly body: string;
  readonly createdAt: number;
};

export const prepareProjectActivityInsert = <
  CompanyId extends string,
  ProjectId extends string,
  TaskId extends string,
  UserId extends string,
  ScopeId extends string = string,
>(
  input: ProjectActivityInsertInput<CompanyId, ProjectId, TaskId, UserId, ScopeId>,
): ProjectActivityInsert<CompanyId, ProjectId, TaskId, UserId, ScopeId> => ({
  companyId: input.companyId,
  businessScopeId: input.projectBusinessScopeId ?? null,
  projectId: input.projectId,
  taskId: input.taskId,
  actorUserId: input.actorUserId,
  kind: input.kind,
  body: input.body,
  createdAt: input.now,
});

export type TaskStatsInput = {
  readonly status: ComplianceTaskStatus;
  readonly dueAt: number | null;
};

export type ComplianceTaskStats = {
  readonly total: number;
  readonly done: number;
  readonly open: number;
  readonly blocked: number;
  readonly overdue: number;
  readonly progress: number;
  readonly byStatus: Record<ComplianceTaskStatus, number>;
};

export type ComplianceProjectCountState =
  | { readonly kind: "loading" }
  | { readonly kind: "loaded"; readonly count: number };

export type ComplianceProjectSummarySource = {
  readonly sourceSummary?: string | null;
  readonly description?: string | null;
};

export type ComplianceProjectUserSource = {
  readonly name?: string | null;
  readonly email?: string | null;
} | null | undefined;

export type ComplianceProjectLeadSource = ComplianceProjectUserSource;

export type ComplianceTaskDisplaySource = {
  readonly description?: string | null;
  readonly update?: {
    readonly summary?: string | null;
    readonly amendingLegislation?: string | null;
  } | null;
  readonly policy?: { readonly title?: string | null } | null;
  readonly assignee?: ComplianceProjectUserSource;
  readonly legislation?: { readonly title?: string | null } | null;
};

export const complianceProjectCountText = (state: ComplianceProjectCountState): string =>
  state.kind === "loading"
    ? "Loading projects..."
    : String(state.count) + " project" + (state.count === 1 ? "" : "s");

const complianceProjectSourceText = (
  project: ComplianceProjectSummarySource,
  fallback: string,
): string => project.sourceSummary ?? project.description ?? fallback;

export const complianceProjectSummaryText = (
  project: ComplianceProjectSummarySource,
): string => complianceProjectSourceText(project, "No source summary recorded.");

export const complianceProjectSourceContextText = (
  project: ComplianceProjectSummarySource,
): string => complianceProjectSourceText(project, "No source context recorded.");

export const complianceProjectUserDisplayName = (
  user: ComplianceProjectUserSource,
  fallback: string,
): string => user?.name ?? user?.email ?? fallback;

export const complianceProjectLeadDisplayName = (
  lead: ComplianceProjectLeadSource,
): string => complianceProjectUserDisplayName(lead, "No lead");

export const complianceProjectCreatedByDisplayName = (
  user: ComplianceProjectUserSource,
): string => complianceProjectUserDisplayName(user, "System");

export const complianceTaskAssigneeDisplayName = (
  task: Pick<ComplianceTaskDisplaySource, "assignee">,
): string => complianceProjectUserDisplayName(task.assignee, "Unassigned");

export const complianceTaskNotesText = (task: ComplianceTaskDisplaySource): string =>
  task.description ?? task.update?.summary ?? "No task notes recorded.";

export const complianceTaskPolicyTitle = (
  task: Pick<ComplianceTaskDisplaySource, "policy">,
): string => task.policy?.title ?? "No document linked";

export const complianceTaskSourceTitle = (task: ComplianceTaskDisplaySource): string =>
  task.legislation?.title ?? task.update?.amendingLegislation ?? "Manual task";

export const complianceProjectUnmatchedImpactCountText = (count: number): string =>
  String(count) + " unmatched impact" + (count === 1 ? "" : "s");

export const complianceProjectBlockedMetricTone = (blocked: number): ComplianceProjectMetricTone =>
  blocked > 0 ? "amber" : "slate";

export const complianceTaskCompletionText = ({
  done,
  total,
}: Pick<ComplianceTaskStats, "done" | "total">): string =>
  String(done) + "/" + String(total) + " tasks complete";

export const complianceProjectOverdueText = (overdue: number): string | null =>
  overdue > 0 ? String(overdue) + " overdue" : null;

export const complianceProjectProgressText = (progress: number): string =>
  String(progress) + "%";

export const complianceProjectProgressWidth = (progress: number): string =>
  complianceProjectProgressText(progress);

export type ComplianceTaskStage<Task> = {
  readonly status: ComplianceTaskStatus;
  readonly label: string;
  readonly tasks: readonly Task[];
};

export type ComplianceTaskReport<Task, Policy> = {
  readonly affectedPolicies: readonly Policy[];
  readonly openTasks: readonly Task[];
  readonly noChangeTasks: readonly Task[];
  readonly approvedTasks: readonly Task[];
};

const emptyStatusCounts = (): Record<ComplianceTaskStatus, number> => ({
  unassigned: 0,
  toReview: 0,
  inProgress: 0,
  readyForApproval: 0,
  approved: 0,
  noChangeRequired: 0,
  blocked: 0,
});

const emptyTaskGroups = <Task>(): Record<ComplianceTaskStatus, readonly Task[]> => ({
  unassigned: [],
  toReview: [],
  inProgress: [],
  readyForApproval: [],
  approved: [],
  noChangeRequired: [],
  blocked: [],
});

export const statsForTasks = (
  tasks: readonly TaskStatsInput[],
  now: number,
): ComplianceTaskStats => {
  const total = tasks.length;
  const done = tasks.filter((task) => taskStatusDone(task.status)).length;
  const blocked = tasks.filter((task) => task.status === "blocked").length;
  const overdue = tasks.filter(
    (task) => !taskStatusDone(task.status) && task.dueAt !== null && task.dueAt < now,
  ).length;
  const byStatus = tasks.reduce<Record<ComplianceTaskStatus, number>>(
    (counts, task) => ({ ...counts, [task.status]: counts[task.status] + 1 }),
    emptyStatusCounts(),
  );

  return {
    total,
    done,
    open: total - done,
    blocked,
    overdue,
    progress: total === 0 ? 0 : Math.round((done / total) * 100),
    byStatus,
  };
};

export const groupTasksByStatus = <Task extends { readonly status: ComplianceTaskStatus }>(
  tasks: readonly Task[],
): Record<ComplianceTaskStatus, readonly Task[]> =>
  tasks.reduce<Record<ComplianceTaskStatus, readonly Task[]>>(
    (groups, task) => ({
      ...groups,
      [task.status]: [...groups[task.status], task],
    }),
    emptyTaskGroups<Task>(),
  );

export const taskStagesFor = <Task extends { readonly status: ComplianceTaskStatus }>(
  tasks: readonly Task[],
): readonly ComplianceTaskStage<Task>[] => {
  const groups = groupTasksByStatus(tasks);
  return COMPLIANCE_TASK_STATUS_ORDER.map((status) => ({
    status,
    label: complianceTaskStatusDisplayName(status),
    tasks: groups[status],
  }));
};

export const complianceTaskReport = <
  Task extends {
    readonly status: ComplianceTaskStatus;
    readonly policy: { readonly _id: string } | null;
  },
>(
  tasks: readonly Task[],
): ComplianceTaskReport<Task, NonNullable<Task["policy"]>> => {
  type Policy = NonNullable<Task["policy"]>;
  const policies = tasks
    .map((task) => task.policy)
    .filter((policy): policy is Policy => policy !== null);
  const affectedPolicies = [...new Map(policies.map((policy) => [policy._id, policy])).values()];

  return {
    affectedPolicies,
    openTasks: tasks.filter((task) => !taskStatusDone(task.status)),
    noChangeTasks: tasks.filter((task) => task.status === "noChangeRequired"),
    approvedTasks: tasks.filter((task) => task.status === "approved"),
  };
};

export const taskStatusDone = (status: ComplianceTaskStatus): boolean =>
  status === "approved" || status === "noChangeRequired";

export const decisionForStatus = (
  status: ComplianceTaskStatus,
  current: ComplianceTaskDecision | null,
): ComplianceTaskDecision | null => {
  if (status === "approved") return current ?? "policyUpdated";
  if (status === "noChangeRequired") return "noChangeRequired";
  if (status === "blocked") return "blocked";
  return current;
};

export type PolicyImpactTaskState = {
  readonly status: ComplianceTaskStatus;
  readonly decision: ComplianceTaskDecision | null;
  readonly completedAt: number | null;
};

export const taskStatusFromPolicyReviewStatus = (
  status: PolicyReviewStatus,
): ComplianceTaskStatus => {
  if (status === "updated") return "approved";
  if (status === "notAffected") return "noChangeRequired";
  return "toReview";
};

export const policyImpactTaskStateFromReviewStatus = (
  status: PolicyReviewStatus,
  now: number,
): PolicyImpactTaskState => {
  const taskStatus = taskStatusFromPolicyReviewStatus(status);
  return {
    status: taskStatus,
    decision: decisionForStatus(taskStatus, null),
    completedAt: taskStatusDone(taskStatus) ? now : null,
  };
};

export const projectStatusLabel = (status: ComplianceProjectStatus): string => {
  const labels: Record<ComplianceProjectStatus, string> = {
    draft: "draft",
    active: "active",
    atRisk: "at risk",
    readyForApproval: "ready for approval",
    complete: "complete",
  };
  return labels[status];
};

export const taskStatusLabel = (status: ComplianceTaskStatus): string => {
  const labels: Record<ComplianceTaskStatus, string> = {
    unassigned: "unassigned",
    toReview: "to review",
    inProgress: "in progress",
    readyForApproval: "ready for approval",
    approved: "approved",
    noChangeRequired: "no change required",
    blocked: "blocked",
  };
  return labels[status];
};

const parseOptionalInputDate = (
  value: string,
  invalidError: ComplianceProjectError,
): Result<number | null, ComplianceProjectError> => {
  const parsed = parseOptionalDateInput(value);
  return parsed.ok ? ok(parsed.value) : err(invalidError);
};
